import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Plus, Trash2, RotateCcw, Save } from "lucide-react";

export default function AdminVSAISettings() {
  const [s, setS] = useState(null);
  const load = () => api.get("/admin/vs-ai/settings").then(r => setS(r.data));
  useEffect(() => { load(); }, []);
  if (!s) return <AdminLayout><div className="text-slate-400">Loading...</div></AdminLayout>;

  const set = (k, v) => setS({ ...s, [k]: v });
  const setKnowledge = (k, v) => setS({ ...s, knowledge_sources: { ...s.knowledge_sources, [k]: v } });
  const addPrompt = () => setS({ ...s, suggested_prompts: [...(s.suggested_prompts||[]), ""] });
  const updatePrompt = (i, v) => {
    const arr = [...s.suggested_prompts]; arr[i] = v; setS({ ...s, suggested_prompts: arr });
  };
  const removePrompt = (i) => setS({ ...s, suggested_prompts: s.suggested_prompts.filter((_, idx) => idx !== i) });

  const save = async () => {
    try {
      const payload = { ...s, suggested_prompts: (s.suggested_prompts||[]).filter(p => p.trim()) };
      await api.put("/admin/vs-ai/settings", payload);
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const reset = async () => {
    if (!window.confirm("Reset VS AI settings to defaults?")) return;
    try { await api.post("/admin/vs-ai/settings/reset"); toast.success("Reset to defaults"); load(); }
    catch (e) { toast.error("Reset failed"); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Bot className="w-6 h-6 text-[#F59E0B]"/>VS AI Settings</h1>
          <p className="text-slate-400 text-sm mt-1">Configure the VS-Solutions intelligent career assistant.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-700 text-slate-200" onClick={reset} data-testid="vsai-reset"><RotateCcw className="w-4 h-4 mr-2"/>Reset</Button>
          <Button className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" onClick={save} data-testid="vsai-save"><Save className="w-4 h-4 mr-2"/>Save Changes</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-6 bg-slate-900 border-slate-800">
          <h3 className="text-lg font-semibold text-white mb-4">General</h3>
          <div className="space-y-3">
            <Toggle label="Enable VS AI" checked={s.enabled} onChange={v=>set("enabled",v)} testId="vsai-enabled"/>
            <Toggle label="Public User Access" checked={s.public_access} onChange={v=>set("public_access",v)} testId="vsai-public"/>
            <Toggle label="Candidate Access" checked={s.candidate_access} onChange={v=>set("candidate_access",v)} testId="vsai-cand"/>
            <Toggle label="Admin/HR Access" checked={s.admin_access} onChange={v=>set("admin_access",v)} testId="vsai-admin"/>
          </div>
          <div className="mt-6">
            <Label className="text-slate-300">Welcome Message</Label>
            <Textarea rows={3} value={s.welcome_message} onChange={e=>set("welcome_message",e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid="vsai-welcome"/>
          </div>
          <div className="mt-4">
            <Label className="text-slate-300">Fallback Message</Label>
            <Textarea rows={3} value={s.fallback_message} onChange={e=>set("fallback_message",e.target.value)} className="bg-slate-800 border-slate-700 text-white"/>
          </div>
        </Card>

        <Card className="p-6 bg-slate-900 border-slate-800">
          <h3 className="text-lg font-semibold text-white mb-4">Suggested Prompts</h3>
          <div className="space-y-2">
            {(s.suggested_prompts||[]).map((p, i) => (
              <div key={i} className="flex gap-2">
                <Input value={p} onChange={e=>updatePrompt(i, e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid={`prompt-${i}`}/>
                <Button size="icon" variant="ghost" className="text-red-400" onClick={()=>removePrompt(i)}><Trash2 className="w-4 h-4"/></Button>
              </div>
            ))}
          </div>
          <Button variant="outline" onClick={addPrompt} className="mt-3 border-slate-700 text-slate-200" data-testid="add-prompt"><Plus className="w-4 h-4 mr-2"/>Add prompt</Button>
        </Card>

        <Card className="p-6 bg-slate-900 border-slate-800 lg:col-span-2">
          <h3 className="text-lg font-semibold text-white mb-4">Knowledge Sources</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(s.knowledge_sources || {}).map(([k, v]) => (
              <Toggle key={k} label={k.replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase())} checked={v} onChange={val=>setKnowledge(k, val)} testId={`ks-${k}`}/>
            ))}
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
      <span className="text-sm text-slate-200">{label}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId}/>
    </div>
  );
}
